import { useQuery } from "@tanstack/react-query";
import type { SigningRequest } from "@vericreator/contracts";
import {
  Bell,
  Apple,
  CheckCircle2,
  Chrome,
  ChevronDown,
  CircleHelp,
  FileVideo,
  LoaderCircle,
  LogOut,
  Plus,
  ScanSearch,
  Settings,
  ShieldCheck,
  Sparkles,
  type LucideIcon
} from "lucide-react";
import { useCallback, useState } from "react";
import { VeriCreatorLogo } from "./components/VeriCreatorLogo";
import { useAuthentication } from "./features/auth/AuthProvider";
import { RequestList } from "./features/studio/RequestList";
import { SigningComposer } from "./features/studio/SigningComposer";
import { useSigningProgress } from "./features/studio/useSigningProgress";
import { api } from "./lib/api";

export default function App() {
  const { status } = useAuthentication();
  if (status === "loading") {
    return <LoadingScreen />;
  }
  return status === "authenticated" ? <Studio /> : <Login />;
}

function LoadingScreen() {
  return (
    <main className="loading-screen">
      <VeriCreatorLogo className="loading-logo" onDark title="VeriCreator" />
      <LoaderCircle className="spin" size={22} />
    </main>
  );
}

function Login() {
  const { identityProviders, loginWithIdentityProvider } = useAuthentication();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("auth") === "failed"
      ? "Não foi possível concluir sua autenticação. Tente novamente."
      : null
  );

  async function begin(provider: "google" | "apple") {
    setError(null);
    setIsSubmitting(true);
    try {
      await loginWithIdentityProvider(provider);
    } catch (reason) {
      setError(
        reason instanceof Error && reason.message === "identity_provider_not_configured"
          ? "Este acesso ainda não está configurado neste ambiente."
          : "Não foi possível iniciar a autenticação. Tente novamente."
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-card" aria-labelledby="login-title">
        <div className="login-brand">
          <VeriCreatorLogo className="login-logo" title="VeriCreator" />
          <span>VeriCreator</span>
        </div>
        <div className="login-heading">
          <span className="eyebrow">Creator studio</span>
          <h1 id="login-title">Entre no seu Studio.</h1>
          <p>Use uma conta que você já conhece. O VeriCreator não recebe nem armazena sua senha.</p>
        </div>
        <div className="identity-providers">
          <button
            className="identity-button google"
            disabled={isSubmitting || !identityProviders.includes("google")}
            onClick={() => void begin("google")}
            type="button"
          >
            {isSubmitting ? <LoaderCircle className="spin" size={18} /> : <Chrome size={18} />} Continuar com Google
          </button>
          <button
            className="identity-button apple"
            disabled={isSubmitting || !identityProviders.includes("apple")}
            onClick={() => void begin("apple")}
            type="button"
          >
            {isSubmitting ? <LoaderCircle className="spin" size={18} /> : <Apple size={18} />} Continuar com Apple
          </button>
        </div>
        {identityProviders.length === 0 && <p className="login-provider-warning" role="status">Os provedores de acesso ainda não foram configurados neste ambiente.</p>}
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="login-proof"><CheckCircle2 size={17} /> Protocolo markerless · V3 Local Crop V2</div>
        <p className="login-footnote">Você poderá conectar YouTube, TikTok ou Instagram separadamente, apenas quando isso for necessário para uma obra.</p>
      </section>
    </main>
  );
}

function Studio() {
  const { session, logout } = useAuthentication();
  const [activeItem, setActiveItem] = useState("Assinar");
  const [latestRequest, setLatestRequest] = useState<SigningRequest | null>(null);
  const [retryingRequestId, setRetryingRequestId] = useState<string | null>(null);
  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(null);
  const [isCancellingAll, setIsCancellingAll] = useState(false);
  const overview = useQuery({
    queryKey: ["studio", session?.accessToken],
    queryFn: () => api.studio(session!.accessToken),
    enabled: Boolean(session?.accessToken),
    staleTime: 30_000
  });

  const data = overview.data;
  const newestRequest = latestRequest && data?.recentRequests.find((request) => request.id === latestRequest.id) || latestRequest;
  const requests = session && newestRequest
    ? [newestRequest, ...(data?.recentRequests ?? []).filter((request) => request.id !== newestRequest.id)]
    : (data?.recentRequests ?? []);
  const refreshAfterTerminalProgress = useCallback(() => {
    void overview.refetch();
  }, [overview.refetch]);
  const signingProgress = useSigningProgress(session?.accessToken ?? "", requests, refreshAfterTerminalProgress);

  if (!session) return null;

  return (
    <div className="studio-shell">
      <aside className="sidebar">
        <div className="sidebar-brand"><VeriCreatorLogo className="sidebar-logo" onDark title="VeriCreator" /><span>VeriCreator</span></div>
        <nav aria-label="Navegação principal">
          <NavItem active={activeItem === "Assinar"} icon={Plus} label="Assinar" onClick={() => setActiveItem("Assinar")} />
          <NavItem active={activeItem === "Biblioteca"} icon={FileVideo} label="Biblioteca" onClick={() => setActiveItem("Biblioteca")} />
          <NavItem active={activeItem === "Verificar"} icon={ScanSearch} label="Verificar" onClick={() => setActiveItem("Verificar")} />
        </nav>
        <div className="sidebar-bottom">
          <button className="nav-item" type="button"><Settings size={18} /> Configurações</button>
          <button className="nav-item" type="button"><CircleHelp size={18} /> Ajuda</button>
          <div className="workspace-card"><span className="workspace-avatar">{session.user.name.slice(0, 1).toUpperCase()}</span><span><strong>{session.user.name}</strong><small>Espaço pessoal</small></span><ChevronDown size={15} /></div>
        </div>
      </aside>

      <main className="studio-main">
        <header className="studio-header">
          <div className="header-title">
            <div className="mobile-brand"><VeriCreatorLogo className="mobile-logo" title="VeriCreator" /> VeriCreator</div>
            <div className="desktop-header-title"><span className="eyebrow">Creator studio</span><strong>Assinar</strong></div>
          </div>
          <section className="header-metrics" aria-label="Resumo de atividade">
            <HeaderMetric detail="no Studio" icon={FileVideo} label="Pedidos" value={data?.metrics.totalRequests ?? "—"} />
            <HeaderMetric accent detail="na fila" icon={LoaderCircle} label="Em processamento" value={data?.metrics.queuedRequests ?? "—"} />
            <HeaderMetric detail="autenticados" icon={CheckCircle2} label="Registros concluídos" value={data?.metrics.completedRequests ?? "—"} />
          </section>
          <div className="header-actions">{data?.isMock && <span className="mock-badge"><Sparkles size={13} /> Ambiente mock</span>}<button aria-label="Notificações" className="icon-button" type="button"><Bell size={19} /></button><button className="logout-button" onClick={() => void logout()} type="button"><LogOut size={17} /> Sair</button></div>
        </header>

        <div className="studio-content">
          <section className="studio-intro">
            <div><span className="eyebrow">Assinar</span><h1>Olá, {session.user.name.split(" ")[0]}.</h1><p>Indique uma fonte pública para iniciar uma assinatura markerless.</p></div>
            <div className="intro-seal"><ShieldCheck size={25} /><span>Seu conteúdo,<br />com referência.</span></div>
          </section>

          {overview.isError ? (
            <section className="notice error"><CircleHelp size={18} /> Não foi possível carregar o painel demonstrativo.</section>
          ) : (
            <>
              <SigningComposer accessToken={session.accessToken} onCreated={(request) => {
                setLatestRequest(request);
                void overview.refetch();
              }} />
              {newestRequest && <section className="notice success"><CheckCircle2 size={19} /><span><strong>Pedido criado.</strong> “{newestRequest.title}” está {newestRequest.status === "queued" ? "na fila para assinatura" : newestRequest.status}.</span></section>}
              <RequestList
                isCancellingAll={isCancellingAll}
                isCancellingRequestId={cancellingRequestId}
                isRetryingRequestId={retryingRequestId}
                progressByRequestId={signingProgress}
                onCancel={(request) => {
                  setCancellingRequestId(request.id);
                  void api.cancelSigningRequest(session.accessToken, request.id)
                    .then(() => {
                      if (latestRequest?.id === request.id) setLatestRequest(null);
                      return overview.refetch();
                    })
                    .catch(() => undefined)
                    .finally(() => setCancellingRequestId(null));
                }}
                onCancelAll={() => {
                  setIsCancellingAll(true);
                  void api.cancelActiveSigningRequests(session.accessToken)
                    .then(({ cancelledRequestIds }) => {
                      if (latestRequest && cancelledRequestIds.includes(latestRequest.id)) setLatestRequest(null);
                      return overview.refetch();
                    })
                    .catch(() => undefined)
                    .finally(() => setIsCancellingAll(false));
                }}
                onRetry={(request) => {
                  setRetryingRequestId(request.id);
                  void api.retrySigningRequest(session.accessToken, request.id)
                    .then((retried) => {
                      setLatestRequest(retried);
                      return overview.refetch();
                    })
                    .catch(() => undefined)
                    .finally(() => setRetryingRequestId(null));
                }}
                requests={requests}
              />
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function HeaderMetric({ label, value, detail, icon: Icon, accent = false }: { label: string; value: number | string; detail: string; icon: LucideIcon; accent?: boolean }) {
  return (
    <article className={accent ? "header-metric accent" : "header-metric"}>
      <span className="header-metric-icon"><Icon className={accent ? "spin" : undefined} size={15} /></span>
      <span className="header-metric-label">{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function NavItem({ active, icon: Icon, label, onClick }: { active: boolean; icon: LucideIcon; label: string; onClick(): void }) {
  return <button className={active ? "nav-item active" : "nav-item"} onClick={onClick} type="button"><Icon size={18} /> {label}</button>;
}
